// components/PerfilVideoPromo.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PerfilVideoPromo.css';

const formatCount = (num) => {
  const n = Number(num) || 0;
  if (n >= 1000000) {
    return Math.round(n / 1000000) + ' M';
  }
  return n.toLocaleString('es-CL');
};

// Obtiene o genera un token unico persistente para este navegador (mismo patron que Video_principal.jsx)
const getViewerToken = () => {
  let token = localStorage.getItem('viewer_token');
  if (!token) {
    token = 'vt_' + Date.now() + '_' + Math.random().toString(36).substring(2, 12);
    localStorage.setItem('viewer_token', token);
  }
  return token;
};

const LikeBadge = ({ liked, count, onClick }) => (
  <div className="badge-wrap">
    <div className="badge" onClick={onClick}>
      <div className="icon-row">
        <i className={`fas fa-heart heart${liked ? ' liked' : ''}`}></i>
        <span className="num">{formatCount(count)}</span>
      </div>
    </div>
  </div>
);

const VistasBadge = ({ count }) => (
  <div className="badge-wrap">
    <div className="badge badge-static">
      <div className="icon-row">
        <i className="fas fa-eye eye-icon"></i>
        <span className="num">{formatCount(count)}</span>
      </div>
    </div>
  </div>
);

const PerfilVideoPromo = ({ perfil, isEditMode, onSave }) => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [likeInfo, setLikeInfo] = useState({ liked: false, count: 0 });
  const [vistasCount, setVistasCount] = useState(0);
  const [showLikeLoginBox, setShowLikeLoginBox] = useState(false);
  const [creadorId, setCreadorId] = useState(null);

  const isUserLoggedIn = !!localStorage.getItem('userToken');

  // Detectar sesion activa de creadora (login normal o via Inspeccionar de Admin)
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${process.env.REACT_APP_AUTH_CREADOR_URL}/check_session.php`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data?.user_id) {
          setCreadorId(data.user_id);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // Cargar estado de "me gusta" y contador de vistas del video promo
  useEffect(() => {
    if (!perfil?.id) return;
    const userId = localStorage.getItem('userId') || 0;
    const controller = new AbortController();
    (async () => {
      try {
        const identidadParam = isUserLoggedIn
          ? `id_viewer=${userId}`
          : creadorId
          ? `id_creador=${creadorId}`
          : `id_viewer=0`;
        const url = `${process.env.REACT_APP_PUBLICACIONES_URL}/get_estado_likes.php?tipo_contenido=video_promo&ids=${perfil.id}&${identidadParam}`;
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();
        if (json?.success && Array.isArray(json.data) && json.data[0]) {
          const item = json.data[0];
          setLikeInfo({ liked: item.liked, count: item.likes_count });
          setVistasCount(item.vistas_count || 0);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error al cargar estado de like/vistas del video promo:', err);
        }
      }
    })();
    return () => controller.abort();
  }, [perfil?.id, isUserLoggedIn, creadorId]);

  const handleToggleLike = async () => {
    if (!isUserLoggedIn && !creadorId) {
      setShowLikeLoginBox(true);
      return;
    }
    const userId = localStorage.getItem('userId');
    setLikeInfo((prev) => ({
      liked: !prev.liked,
      count: prev.liked ? Math.max(prev.count - 1, 0) : prev.count + 1,
    }));
    try {
      const formData = new FormData();
      if (isUserLoggedIn) {
        formData.append('ID_U_viewer', userId);
      } else {
        formData.append('ID_U_creador', creadorId);
      }
      formData.append('tipo_contenido', 'video_promo');
      formData.append('contenido_id', perfil.id);
      const response = await fetch(`${process.env.REACT_APP_PUBLICACIONES_URL}/toggle_like.php`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setLikeInfo({ liked: result.liked, count: result.likes_count });
      }
    } catch (err) {
      console.error('Error al dar like al video promo:', err);
    }
  };

  const registrarVista = () => {
    if (!perfil?.id) return;
    const formData = new FormData();
    formData.append('tipo_contenido', 'video_promo');
    formData.append('contenido_id', perfil.id);
    formData.append('token', getViewerToken());
    fetch(`${process.env.REACT_APP_PUBLICACIONES_URL}/registrar_vista.php`, {
      method: 'POST',
      body: formData,
    })
      .then((res) => res.json())
      .then((result) => {
        if (result?.success && result?.contado) {
          setVistasCount((prev) => prev + 1);
        }
      })
      .catch((err) => console.error('Error al registrar vista del video promo:', err));
  };

  const handleVideoClick = () => {
    setIsModalOpen(true);
    registrarVista();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar que sea un video
    if (!file.type.startsWith('video/')) {
      alert('Por favor selecciona un archivo de video v�lido');
      return;
    }

    const formData = new FormData();
    formData.append('video_promo', file);
    formData.append('publicacion_id', perfil.id);

    try {
      const response = await fetch(`${process.env.REACT_APP_PERFILES_URL}/update_video.php`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      if (result.success) {
        alert('Video subido exitosamente');
        window.location.reload();
      }
    } catch (error) {
      console.error('Error al subir video:', error);
      alert('Error al subir el video');
    }
  };

  // ? URL del video desde la carpeta correcta
  const videoUrl = perfil.video
  ? `${process.env.REACT_APP_UPLOADS_URL}/ID_${perfil.id}/${perfil.video}?v=${perfil.id}`
  : null;

  return (
    <div className="video-promo-wrapper">
    <div className="profile-video">
      {!isPlaying && !videoUrl ? (
        // PLACEHOLDER - cuando no hay video
        <>
          <img
            src={`${process.env.REACT_APP_UPLOADS_URL}/logotipo_new8.jpg`}
            alt="Ulalaaa Logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
          {isEditMode && (
            <div
              className="edit-overlay"
              onClick={() => setShowUpload(true)}
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            >
              <i className="fas fa-upload"></i>
              <span>Subir Video</span>
            </div>
          )}
        </>
      ) : !isPlaying && videoUrl ? (
        // ? PREVIEW - muestra miniatura y bot�n play (DIMENSIONES ORIGINALES)
        <div
          className="custom-video-player"
          style={{ display: 'block' }}
          onClick={handleVideoClick}
        >
          <video key={perfil.id} style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
            <source src={videoUrl} type="video/mp4" />
          </video>
          <div
            className="video-icon"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}
          >
            <i className="fas fa-play-circle"></i>
          </div>
          {isEditMode && (
            <button
              className="change-video-btn"
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: '#375BD2',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                padding: '5px 10px',
                cursor: 'pointer',
                zIndex: 20
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowUpload(true);
              }}
            >
              <i className="fas fa-sync-alt"></i> Cambiar
            </button>
          )}
        </div>
) : null}
    </div>

      {videoUrl && (
        <div className="video-promo-badge-row">
          <LikeBadge liked={likeInfo.liked} count={likeInfo.count} onClick={handleToggleLike} />
          <VistasBadge count={vistasCount} />
        </div>
      )}

      {/* Modal overlay de reproducción */}
      {isModalOpen && videoUrl && (
        <div
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '90%',
              maxWidth: '1200px',
              backgroundColor: '#1a1a1a',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 10px 50px rgba(0,0,0,0.8)'
            }}
          >
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                color: '#FFFFFF',
                fontSize: '20px',
                cursor: 'pointer',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
            <video
              controls
              autoPlay
              controlsList="nodownload"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                display: 'block',
                borderRadius: '8px'
              }}
            >
              <source src={videoUrl} type="video/mp4" />
              Tu navegador no soporta el elemento de video.
            </video>
            <div className="video-promo-badge-row video-promo-badge-row-modal">
              <LikeBadge liked={likeInfo.liked} count={likeInfo.count} onClick={handleToggleLike} />
              <VistasBadge count={vistasCount} />
            </div>
          </div>
        </div>
      )}

      {showLikeLoginBox && (
        <div className="seguir-login-overlay" onClick={() => setShowLikeLoginBox(false)}>
          <div className="login-required-box" onClick={(e) => e.stopPropagation()}>
            <i className="fas fa-lock"></i>
            <h3>Para dar me gusta debes iniciar sesión</h3>
            <button
              className="btn-inicia-sesion"
              onClick={() => {
                localStorage.setItem('returnToProfile', perfil.id);
                navigate('/acceso_viewer');
              }}
            >
              INICIA SESIÓN
            </button>
          </div>
        </div>
      )}

      {showUpload && isEditMode && (
        <div
          className="upload-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: '#6A1710',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '500px',
              width: '90%'
            }}
          >
            <h3 style={{ color: 'white', marginBottom: '20px', textAlign: 'center' }}>
              Subir Video Promocional
            </h3>
            <input
              type="file"
              accept="video/*"
              onChange={handleFileUpload}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(246,59,59,0.3)',
                borderRadius: '5px',
                color: 'white',
                cursor: 'pointer'
              }}
            />
            <button
              onClick={() => setShowUpload(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#8A1C07',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerfilVideoPromo;
